import { CaretDownIcon, UsersIcon } from "@phosphor-icons/react";
import { useState } from "react";
import { useWatch } from "react-hook-form";
import { useIntl } from "react-intl";

import {
  DrawerContent,
  DrawerTopBar,
  SwipeableDrawer,
} from "@/components/Drawer";
import { TextField } from "@/components/TextField/TextField";
import type { HotelSearchFormInputValues } from "@/features/hotels/utils/hotelsValidation";
import {
  HotelSearchTrackingEvents,
  rudderAnalytics,
  type SearchSource,
} from "@/lib/rudderstack";

import { VisitorsSelection } from "../components/VisitorsSelections";
import type { HotelSearchForm } from "../HotelSearchForm";

type HotelVisitorsPickerProps = {
  form: HotelSearchForm;
  analyticsSource: SearchSource;
  isCompact: boolean;
  onChange?: (values: HotelSearchFormInputValues) => void;
};

export function HotelVisitorsPicker({
  form,
  analyticsSource,
  isCompact,
  onChange,
}: HotelVisitorsPickerProps) {
  const intl = useIntl();

  const [isDrawerOpen, setIsDrawerOpen] = useState(false);

  const rooms = useWatch({ control: form.control, name: "rooms" });

  return (
    <>
      <TextField
        fullWidth
        label={intl.formatMessage({
          defaultMessage: "Guests, Rooms",
          description: "Short label for the guests and rooms picker",
        })}
        value={intl.formatMessage(
          {
            defaultMessage:
              "{visitorsCount, plural, one {# guest} other {# guests}}, {rooms, plural, one {# room} other {# rooms}}",
            description: "Compact summary of total guests and rooms",
          },
          {
            visitorsCount: rooms.reduce(
              (acc, room) => acc + room.adults + room.children,
              0,
            ),
            rooms: rooms.length,
          },
        )}
        startAdornment={!isCompact && <UsersIcon />}
        endAdornment={<CaretDownIcon />}
        onClick={() => {
          rudderAnalytics.track(
            HotelSearchTrackingEvents.USER_CLICKED_HOTEL_TRAVELLERS_INPUT,
            {
              source: analyticsSource,
            },
          );

          setIsDrawerOpen(true);
        }}
      />

      <SwipeableDrawer
        hasFullHeight
        onClose={() => setIsDrawerOpen(false)}
        open={isDrawerOpen}
        disableRestoreFocus
      >
        <DrawerTopBar
          onCloseClick={() => setIsDrawerOpen(false)}
          title={intl.formatMessage({
            defaultMessage: "Guests & Rooms",
            description:
              "Label for the guests and rooms picker in hotel search",
          })}
        />

        <DrawerContent>
          <VisitorsSelection
            value={rooms}
            onConfirm={(rooms) => {
              rudderAnalytics.track(
                HotelSearchTrackingEvents.USER_CHANGED_HOTEL_TRAVELLERS,
                {
                  source: analyticsSource,
                  // for now we are not supporting multiple rooms booking so we send only first room data
                  travellers: rooms[0],
                },
              );

              form.setValue("rooms", rooms);
              onChange?.(form.getValues());
              setIsDrawerOpen(false);
            }}
          />
        </DrawerContent>
      </SwipeableDrawer>
    </>
  );
}
